<template>
  <v-card>
    <v-card-title>
      {{ trans('booking.surveyInformation') }}
    </v-card-title>

    <v-card-text>
      <form-input
        :label="trans('survey.status')"
        cols="6"
        readonly
      >
        <template v-slot:input>
          <survey-status :booking="booking" :survey-result="surveyResult" :full-text="true"/>
        </template>
      </form-input>

      <form-input
        v-if="surveyResult && surveyResult.lastEmail"
        :label="trans('emailSettings.sentAt')"
        :value="formatDate(surveyResult.lastEmail.successfullySentAt, 'LLL')"
        cols="3"
        readonly
      />

      <form-input
        v-if="surveyResult"
        :label="trans('survey.firstOpenedAt')"
        :value="formatDate(surveyResult.firstOpenedAt, 'LLL')"
        cols="3"
        readonly
      />

      <form-input
        v-if="surveyResult"
        :label="trans('survey.answeredAt')"
        :value="formatDate(surveyResult.answeredAt, 'LLL')"
        cols="3"
        readonly
      />

      <form-input
        v-if="surveyResult"
        :label="trans('survey.reviewedAt')"
        :value="formatDate(surveyResult.reviewedAt, 'LLL')"
        cols="3"
        readonly
      />

      <form-input
        cols="8"
        readonly
      >
        <template v-slot:input>
          <v-btn
            small
            v-if="surveyResult && surveyResult.answeredAt && showViewResultButton"
            @click="pushToRoute('SurveyResultPage', {bookingId: booking.id})"
            class="mr-2"
          >{{ trans('survey.viewSurveyResult') }}</v-btn>
          <v-btn
            small
            v-if="canSurveyResultBeUpdated(surveyResult) && surveyResult.status === 'ANSWERED'"
            @click="toggleSurveyReviewedAndReload"
            :loading="loadingSurveyResult"
            class="mr-2"
            :color="showViewResultButton ? null : 'primary'"
          ><v-icon left small>mdi-check</v-icon>{{ trans('survey.setReviewed') }}</v-btn>
          <v-btn
            small
            v-if="canSurveyResultBeUpdated(surveyResult) && surveyResult.status === 'REVIEWED'"
            @click="toggleSurveyReviewedAndReload"
            :loading="loadingSurveyResult"
            class="mr-2"
          >{{ trans('survey.setNotReviewed') }}</v-btn>
          <v-btn
            small
            v-if="surveyResult && surveyResult.lastEmail"
            @click="getEmail(surveyResult.lastEmail)"
            class="mr-2"
          >{{ trans('emailSettings.showEmail') }}
          </v-btn>
          <v-btn
            small
            v-if="canSurveyEmailBeSent(booking)"
            @click="sendSurveyMailAndReload"
            :loading="loadingSendSurveyMailAndReload"
            class="mr-2"
          >{{ trans((surveyResult && surveyResult.lastEmail) ? 'survey.sendEmailAgain' : 'survey.sendEmail') }}</v-btn>
          <v-btn
            small
            v-if="canAutoSurveyBeActivated(booking)"
            @click="toggleBookingSurveyStatusAndReload"
            :loading="loadingSurveyResult"
            class="mr-2"
          >{{ trans('survey.enableSending') }}</v-btn>
          <v-btn
            small
            v-if="canAutoSurveyBeDeactivated(booking)"
            @click="toggleBookingSurveyStatusAndReload"
            :loading="loadingSurveyResult"
            class="mr-2"
          >{{ trans('survey.disableSending') }}</v-btn>
        </template>
      </form-input>
    </v-card-text>

    <validation-errors-modal
      :title="trans('error.general')"
      :show-modal.sync="showSurveyEmailErrorModal"
      :validation-errors.sync="surveyEmailValidationErrors"
    />

    <email-modal
      :email="currentEmail"
      :show-modal.sync="showEmailModal"
    ></email-modal>
  </v-card>
</template>

<script>
import {LocaleUtils} from '@/utils/locale-utils';
import {DateUtils} from '@/utils/date-utils';
import {EmailUtils} from '@/utils/email-utils';
import EmailModal from '@/components/EmailModal';
import {SurveyUtils} from '@/utils/survey-utils';
import FormInput from '@/components/FormInput';
import SurveyStatus from '@/components/SurveyStatus';
import {RouterUtils} from '@/utils/router-utils';
import ValidationErrorsModal from './ValidationErrorsModal';
import {SurveyApi} from '@/api/survey-api';

export default {
  name: 'SurveyResultCard',
  components: {ValidationErrorsModal, SurveyStatus, FormInput, EmailModal},
  mixins: [
    LocaleUtils,
    DateUtils,
    EmailUtils,
    SurveyUtils,
    RouterUtils
  ],
  props: {
    booking: {
      type: Object,
      required: true
    },
    surveyResult: {
      required: true
    },
    showViewResultButton: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      loadingSendSurveyMailAndReload: false,
      loadingSurveyResult: false,

      showSurveyEmailErrorModal: false,
      surveyEmailValidationErrors: null,
    }
  },
  methods: {
    sendSurveyMailAndReload () {
      this.loadingSendSurveyMailAndReload = true;
      this.sendSurveyMail(this.booking)
        .catch(error => {
          this.surveyEmailValidationErrors = this.getValidationErrors(error, false);
          this.showSurveyEmailErrorModal = true;
        })
        .then(() => this.$emit('update'))
        .finally(() => {
          this.loadingSendSurveyMailAndReload = false;
        });
    },

    toggleBookingSurveyStatusAndReload () {
      this.loadingSurveyResult = true;
      this.toggleBookingSurveyStatus(this.booking)
        .then(() => this.$emit('update'))
        .finally(() => {
          this.loadingSurveyResult = false;
        });
    },

    toggleSurveyReviewedAndReload () {
      this.loadingSurveyResult = true;
      this.toggleSurveyReviewed(this.surveyResult)
        .then(() => this.$emit('update'))
        .finally(() => {
          this.loadingSurveyResult = false;
        });
    }
  }
}
</script>

<style scoped>

</style>
